import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Alert, Spinner, Pagination } from '@glplus/glpweb-dsl';
import { vendorApiClient, type QuestionListItem, type QuestionsListResponse } from '../../lib/vendorApiClient';
import { BORDER, GREEN_INK, HEADING, MUTED, FieldLabel, ProductStatusChip, ProductThumb, departmentPath, describeProductError } from './parts';
import { QuestionActions, outcomeNotice, ourBarcodeKey, reasonKey, sentBarcode, sentPlace, sentSource, suggestedTitle } from './questionParts';
import MissingInfoForm from './MissingInfo';

/**
 * Q1 — "Needs your answer" inside Products (Figma 62:2). There is no separate
 * confirmation tab (§15u): questions are a filter of the products list, and
 * each one is a yes/no about our ONE best guess (§15t) — never a pick-list.
 */

const PAGE_SIZE = 10;

export default function QuestionCards({
  vendorId,
  canResolve,
  page,
  onPage,
  onChanged,
}: {
  vendorId: string;
  canResolve: boolean;
  page: number;
  onPage: (page: number) => void;
  /** A question was dealt with — the list's counts have moved. */
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [data, setData] = useState<QuestionsListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const seq = useRef(0);
  const load = useCallback(() => {
    const mine = ++seq.current;
    setError(null);
    vendorApiClient
      .listQuestions(vendorId, { page, pageSize: PAGE_SIZE })
      .then((r) => mine === seq.current && setData(r))
      .catch((e: Error) => mine === seq.current && setError(describeProductError(e, t)));
  }, [vendorId, page, t]);
  useEffect(load, [load]);

  if (error && !data) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));
  const first = data.total === 0 ? 0 : (data.page - 1) * data.pageSize + 1;
  const last = Math.min(data.total, data.page * data.pageSize);

  return (
    <Box data-testid="questions">
      {notice && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setNotice(null)} data-testid="questions-notice">
          {notice}
        </Alert>
      )}
      {data.total === 0 ? (
        <Typography sx={{ mt: 3, fontSize: 15, color: MUTED }} data-testid="questions-none">
          {t('products.questions.none')}
        </Typography>
      ) : (
        <>
          <Alert severity="warning" title={t('products.questions.banner.title', { count: data.total })} sx={{ mt: 2 }} data-testid="questions-banner">
            {t('products.questions.banner.body')}
          </Alert>
          {data.items.map((q) => (
            <QuestionCard
              key={q.questionId}
              vendorId={vendorId}
              question={q}
              canResolve={canResolve}
              onDone={(text) => {
                setNotice(text);
                // The last card of a page leaves an empty page behind it.
                if (data.items.length === 1 && page > 1) onPage(page - 1);
                else load();
                window.scrollTo({ top: 0 });
                onChanged();
              }}
            />
          ))}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 14, color: MUTED }} data-testid="questions-range">
              {t('products.questions.showing', { first, last, count: data.total })}
            </Typography>
            {totalPages > 1 && (
              <Box sx={{ ml: 'auto' }}>
                <Pagination
                  page={data.page}
                  totalPages={totalPages}
                  onPageChange={onPage}
                  ariaLabel={t('products.questions.pagination')}
                  activeBgColor="#25862D"
                  activeColor="#FFFFFF"
                  align="right"
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

function QuestionCard({
  vendorId,
  question,
  canResolve,
  onDone,
}: {
  vendorId: string;
  question: QuestionListItem;
  canResolve: boolean;
  onDone: (notice: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const { sent, suggested } = question;
  const missing = question.missing ?? [];
  const name = sent?.name ?? suggested?.name ?? '';
  const details = (
    <Box
      component={Link}
      to={`/products/${question.vendorProductId}`}
      data-testid={`question-details-${question.questionId}`}
      sx={{ fontSize: 15, fontWeight: 700, color: GREEN_INK, textDecoration: 'none', px: 1 }}
    >
      {t('products.questions.details')}
    </Box>
  );

  return (
    <Box
      component="section"
      aria-label={t('products.questions.cardAria', { name })}
      data-testid={`question-${question.questionId}`}
      sx={{ mt: 2, p: 2.5, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography component="h2" sx={{ flex: 1, fontSize: 17, fontWeight: 700, color: HEADING }}>
          {suggested ? t('products.questions.sameProduct') : missing.length > 0 ? t('products.missing.title', { name }) : t('products.questions.needMore', { name })}
        </Typography>
        <ProductStatusChip status="needs_answer" />
      </Box>
      {!suggested && missing.length > 0 && (
        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ flex: 1, fontSize: 13, color: MUTED }}>
            {[sent?.size, sent ? (sentPlace(t, sent) ?? sentSource(t, sent)) : null].filter(Boolean).join(' · ')}
          </Typography>
          {details}
        </Box>
      )}

      {suggested && sent ? (
        <>
          <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' }, gap: 2, alignItems: 'center' }}>
            <Box sx={{ p: 2, bgcolor: '#F7F7F8', borderRadius: '8px', display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
              <ProductThumb src={null} size={56} />
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel>{t('products.questions.youSent')}</FieldLabel>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING }}>{sent.name}</Typography>
                {sent.size && <Typography sx={{ fontSize: 13, color: MUTED }}>{sent.size}</Typography>}
                <Typography sx={{ fontSize: 13, color: MUTED }}>{[sentBarcode(t, sent), sentSource(t, sent)].join(' · ')}</Typography>
              </Box>
            </Box>
            <Typography aria-hidden sx={{ fontSize: 20, fontWeight: 700, color: MUTED, textAlign: 'center' }}>
              =?
            </Typography>
            <Box sx={{ p: 2, bgcolor: '#EAF5EA', borderRadius: '8px', display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
              <ProductThumb src={suggested.imageUrl} size={56} />
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel>{t('products.questions.weThink')}</FieldLabel>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING }}>{suggestedTitle(suggested)}</Typography>
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  {[suggested.size, departmentPath(t, i18n.language, suggested.department, suggested.category)]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                <Typography sx={{ fontSize: 13, color: MUTED }} data-testid={`question-our-barcode-${question.questionId}`}>
                  {t(`products.questions.ourBarcode.${ourBarcodeKey(question)}`)}
                </Typography>
              </Box>
            </Box>
          </Box>
          {reasonKey(question) === 'staff_proposal' && (
            <Typography sx={{ mt: 1.5, fontSize: 14, color: HEADING }} data-testid={`question-from-us-${question.questionId}`}>
              {question.note ? t('products.questions.fromUsNote', { note: question.note }) : t('products.questions.fromUs')}
            </Typography>
          )}
          <QuestionActions
            vendorId={vendorId}
            question={question}
            canResolve={canResolve}
            detailsTo={details}
            onDone={(outcome) => onDone(outcomeNotice(t, sent.name, outcome))}
          />
        </>
      ) : missing.length > 0 ? (
        <MissingInfoForm
          // What is left to ask changes with each answer; a fresh form for each set.
          key={missing.join('|')}
          vendorId={vendorId}
          vendorProductId={question.vendorProductId}
          missing={missing}
          canResolve={canResolve}
          onDone={(result) => onDone(t(result.remaining.length === 0 ? 'products.questions.done.confirmed' : 'products.missing.savedMore', { name }))}
        />
      ) : (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ flex: 1, fontSize: 15, color: MUTED }}>{t('products.questions.openToAnswer')}</Typography>
          {details}
        </Box>
      )}
    </Box>
  );
}
